import { makeAutoObservable, reaction, toJS } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { countryService } from "../services/countryService";
import {
  CountryValuesInit,
  getCountryUpdateVariables,
  getCountryValues,
  CountryValuesType,
  CountryErrorsType,
  countryDisplayId,
} from "./CountryCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { Country } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class CountryUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: CountryErrorsType = {};
  service = countryService;
  countryInstance: Country | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  // Se guardó, pero el formulario no se pudo poner al día (la relectura falló, o
  // el formulario cambió mientras se guardaba): Guardar queda deshabilitado hasta
  // recargar (OutdatedAfterSaveNotice), así nunca se guarda desde un estado viejo.
  savedButOutdated: boolean = false;
  // En ese estado, lo que se escribe cuenta como cambio pendiente: la foto del
  // formulario cubre los campos; este vigía, también las grillas y los archivos.
  private editedWhileOutdated: boolean = false;
  private stopOutdatedWatch?: () => void;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = CountryValuesInit();
  name_esError: string | undefined;
  name_enError: string | undefined;
  name_ptError: string | undefined;
  codeError: string | undefined;
  currencyError: string | undefined;
  demonym_esError: string | undefined;
  demonym_enError: string | undefined;
  demonym_ptError: string | undefined;

  private customValidators: CustomValidator<CountryValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(v as CustomValidator<CountryValuesType>);
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    // Primero, lejos de lo que un proyecto agrega después del bloque de stopReaction.
    this.stopOutdatedWatch?.();
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<CountryValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadCountryById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.countryInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.countryInstance) {
      // Lo que se ve es lo que hay en el server: el formulario está al día.
      this.becomeCurrent();
      this.entityValues = getCountryValues(
        this.countryInstance,
        this.langStore
      );
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda, y con el formulario guardado pero desactualizado.
  get saveDisabled() {
    return this.isProcessing || this.savedButOutdated;
  }

  /**
   * Vuelve a leer el registro: la acción del aviso de guardado pero desactualizado.
   * Si la lectura sale bien el formulario queda al día y Guardar se vuelve a
   * habilitar; si no, el error queda a la vista y el aviso sigue.
   */
  *reloadCountry(): any {
    if (this.isProcessing) return;
    if (this.pendingChanges) {
      // Lo escrito después de quedar desactualizado se descartaría: se pregunta,
      // como al salir.
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.reloadWithoutSaving,
        this.executeReloadAction.bind(this)
      );
      return;
    }
    yield this.executeReloadAction();
  }

  *executeReloadAction(): any {
    yield this.process(() => this.loadCountryById(this.entityValues.id));
  }

  /**
   * Queda guardado pero desactualizado. Se toma la foto del formulario: lo editado
   * antes de guardar ya se guardó y no cuenta; lo que se escriba desde ahora cuenta
   * como cambio pendiente, y Cancelar, la navegación y Recargar avisan antes de
   * descartarlo. Para lo que la foto no cubre (las grillas y los archivos), el
   * vigía lo marca.
   */
  private becomeOutdated() {
    this.savedButOutdated = true;
    this.resetPendingChanges();
    this.saveOriginalState();
    this.editedWhileOutdated = false;
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = reaction(
      () => ({ ...toJS(this.entityValues), version_: undefined }),
      () => {
        this.editedWhileOutdated = true;
      },
      { equals: (a, b) => _.isEqual(a, b) }
    );
  }

  /** El formulario está al día (se volvió a leer el registro): se sale del estado. */
  private becomeCurrent() {
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = undefined;
    this.editedWhileOutdated = false;
    this.savedButOutdated = false;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return countryDisplayId(this.entityValues as any, this.langStore);
    }
    if (this.countryInstance) {
      return countryDisplayId(this.countryInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.CountrySingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupCountryLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  returnedData(_data: any): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  getName_esValue(): string {
    return this.entityValues.name_es;
  }

  setName_esValue(value: string) {
    this.entityValues.name_es = value;
  }

  getName_enValue(): string {
    return this.entityValues.name_en;
  }

  setName_enValue(value: string) {
    this.entityValues.name_en = value;
  }

  getName_ptValue(): string {
    return this.entityValues.name_pt;
  }

  setName_ptValue(value: string) {
    this.entityValues.name_pt = value;
  }

  getCodeValue(): string {
    return this.entityValues.code;
  }

  setCodeValue(value: string) {
    this.entityValues.code = value;
  }

  getCurrencyValue(): string {
    return this.entityValues.currency;
  }

  setCurrencyValue(value: string) {
    this.entityValues.currency = value;
  }

  getDemonym_esValue(): string {
    return this.entityValues.demonym_es;
  }

  setDemonym_esValue(value: string) {
    this.entityValues.demonym_es = value;
  }

  getDemonym_enValue(): string {
    return this.entityValues.demonym_en;
  }

  setDemonym_enValue(value: string) {
    this.entityValues.demonym_en = value;
  }

  getDemonym_ptValue(): string {
    return this.entityValues.demonym_pt;
  }

  setDemonym_ptValue(value: string) {
    this.entityValues.demonym_pt = value;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.countryInstance = null;
    this.entityValues = CountryValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: CountryValuesType): CountryErrorsType {
    const errors: CountryErrorsType = {};
    this.clearErrors();
    values.name_es = values.name_es.trim();
    if (values.name_es === "") {
      errors.name_es = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.CountryNameLabel,
      };
    }
    values.name_en = values.name_en.trim();
    if (values.name_en === "") {
      errors.name_en = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.CountryNameLabel,
      };
    }
    values.name_pt = values.name_pt.trim();
    if (values.name_pt === "") {
      errors.name_pt = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.CountryNameLabel,
      };
    }
    values.name_es = values.name_es.trim();
    if (values.name_es === "") {
      errors.name_es = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.CountryNameLabel,
      };
    }
    values.name_en = values.name_en.trim();
    if (values.name_en === "") {
      errors.name_en = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.CountryNameLabel,
      };
    }
    values.name_pt = values.name_pt.trim();
    if (values.name_pt === "") {
      errors.name_pt = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.CountryNameLabel,
      };
    }
    values.name_es = values.name_es.trim();
    if (values.name_es === "") {
      errors.name_es = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.CountryNameLabel,
      };
    }
    values.name_en = values.name_en.trim();
    if (values.name_en === "") {
      errors.name_en = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.CountryNameLabel,
      };
    }
    values.name_pt = values.name_pt.trim();
    if (values.name_pt === "") {
      errors.name_pt = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.CountryNameLabel,
      };
    }
    values.code = values.code.trim();
    if (values.code === "") {
      errors.code = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.CountryCodeLabel,
      };
    }
    values.currency = values.currency.trim();
    if (values.currency === "") {
      errors.currency = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.CountryCurrencyLabel,
      };
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "CountryUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    // Guardado pero desactualizado: cuenta lo escrito desde que se entró en ese
    // estado (la foto, y el vigía para lo que la foto no cubre), no las filas y
    // los archivos que ya se guardaron.
    if (this.savedButOutdated)
      return this._ownPendingChanges || this.editedWhileOutdated;
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name_es: this.entityValues.name_es,
      name_en: this.entityValues.name_en,
      name_pt: this.entityValues.name_pt,
      code: this.entityValues.code,
      currency: this.entityValues.currency,
      demonym_es: this.entityValues.demonym_es,
      demonym_en: this.entityValues.demonym_en,
      demonym_pt: this.entityValues.demonym_pt,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }

    return changed;
  }

  *saveCountry(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados. Tampoco con el formulario
    // guardado pero desactualizado: no se guarda desde un estado viejo.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Mientras se guarda, la capa no deja editar. Si el formulario cambia igual
      // (algún camino programático), lo que se ve ya no es lo que se guardó, y no
      // se concilia nada: queda guardado pero desactualizado, como si la relectura
      // fallara. La versión nueva, que _save toma de la respuesta, no cuenta.
      let changedWhileSaving = false;
      const stopWatching = reaction(
        () => ({ ...toJS(this.entityValues), version_: undefined }),
        () => {
          changedWhileSaving = true;
        },
        { equals: (a, b) => _.isEqual(a, b) }
      );
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        } finally {
          stopWatching();
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            // El formulario queda con el registro del server, no con lo que mandó:
            // las filas nuevas toman su id, los archivos quedan guardados (no
            // "added", que se volverían a mandar) y version_ es el que quedó. Si el
            // formulario sigue abierto (volver atrás no navega, por ejemplo en una
            // pestaña nueva), el próximo guardado parte de ahí. Si no se puede poner
            // al día (cambió mientras se guardaba, o la relectura falla), el guardado
            // ya está hecho, pero lo que se ve no es lo que hay: queda guardado pero
            // desactualizado, con Guardar deshabilitado y el aviso con Recargar, y
            // no se cierra.
            if (changedWhileSaving) {
              this.becomeOutdated();
            } else {
              try {
                yield this.loadCountryById(this.savedId);
              } catch (error) {
                console.warn(
                  "[CountryUpdateStore] saved, but reloading the record failed:",
                  error
                );
                this.becomeOutdated();
              }
            }
            if (!this.savedButOutdated) {
              this.resetPendingChanges();
              this.saveOriginalState();
            }
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          if (!this.savedButOutdated)
            this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelCountry(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getCountryValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = CountryValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    if (this.countryInstance) {
      const variables = getCountryUpdateVariables(this.entityValues);
      this.savedId = null;
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.countryUpdate({
        ...variables,
        idempotencyKey_: this.idempotencyKeyFor(variables),
      });
      this.savedId = result.countryUpdate.id;
      // La versión nueva la da el server: si el formulario sigue abierto (volver
      // atrás no navega, por ejemplo en una pestaña nueva), el próximo guardado
      // la manda y no choca consigo mismo.
      this.entityValues.version_ = result.countryUpdate.version_;
      this.saveAttempt = null;
      this.countryInstance = result.countryUpdate as Country;
    }
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadCountryById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
